/**
 * ProtectedRoute — React Router v6 layout guard for the Training Portal.
 *
 * Placement in the route tree (see App.tsx):
 *
 *   <Route element={<ProtectedRoute />}>   ← this component
 *     <Route element={<AppShell />}>
 *       <Route path="scenarios" element={<ScenarioSelectPage />} />
 *       ...
 *     </Route>
 *   </Route>
 *
 * Guards:
 *   1. No access_token in sessionStorage → redirect /login
 *   2. Role is `admin` only (wrong portal — no training access) →
 *      clear session + redirect /login with a hint message
 *
 * Roles `agent` and `manager` both have training portal access (see ADR-007).
 *
 * This component renders <Outlet /> when the session is valid, so React Router
 * continues resolving nested routes (AppShell → page components).
 *
 * NOTE: sessionStorage is read synchronously on every render. There is no
 * async loading state — the guard is instantaneous.
 */

import { Navigate, Outlet } from 'react-router-dom';

export function ProtectedRoute(): JSX.Element {
  const token = sessionStorage.getItem('access_token');
  const role = sessionStorage.getItem('user_role');

  // Guard 1: not authenticated
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Guard 2: authenticated as admin-only — wrong portal
  // (admin role has no training portal access per ADR-007)
  if (role === 'admin') {
    sessionStorage.clear();
    return <Navigate to="/login" replace />;
  }

  // Authenticated as agent or manager — render nested routes
  return <Outlet />;
}
